// components/workspace-timeline/useWorkspaceTimelineContextMenuController.ts
import React, { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import type { KernelHandle, QuickMenuAction } from '#types/types';
import type { PendingVideoGenerationTrack } from '#lib/timeline-generation-placeholders';
import {
  MIN_TIMELINE_TRACK_SPAN_MS,
  TIMELINE_EFFECT_ENABLED_EPSILON,
  normalizeEffectRatio,
} from '#lib/workspace-timeline';
import { clamp } from './presentation';
import type { ResolvedWorkspaceTimelineTrack } from './types';

const MIN_TRACK_SPAN_MS = MIN_TIMELINE_TRACK_SPAN_MS;

interface UseWorkspaceTimelineContextMenuControllerArgs {
  kernel: React.RefObject<KernelHandle | null>;
  projectId: string;
  workspaceId: string;
  resolvedTracks: ResolvedWorkspaceTimelineTrack[];
  separatingSoundTrackIdSet: Set<string>;
  mergingTrackIdSet: Set<string>;
  pendingVideoGenerationTracks: PendingVideoGenerationTrack[];
  clampedCursorMsRef: React.MutableRefObject<number>;
  volumeEditorTrackIdRef: React.MutableRefObject<string | null>;
  seekPlaybackCursor: (nextCursorMs: number) => void;
  setSplitPreview: React.Dispatch<React.SetStateAction<{ trackId: string; splitMs: number } | null>>;
  splitTrackAtMs: (track: ResolvedWorkspaceTimelineTrack, splitCursorMsRaw: number) => void;
  separateTrackAudio: (track: ResolvedWorkspaceTimelineTrack) => Promise<void>;
  mergeSelectedVideoTracks: () => Promise<void>;
  openVolumeEditorForTrack: (track: ResolvedWorkspaceTimelineTrack) => void;
  closeVolumeEditor: () => void;
}

interface UseWorkspaceTimelineContextMenuControllerResult {
  handleTrackTimelineToolContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    track: ResolvedWorkspaceTimelineTrack
  ) => void;
  handlePendingTrackContextMenu: (event: React.MouseEvent, taskId: string) => void;
}

export const useWorkspaceTimelineContextMenuController = ({
  kernel,
  projectId,
  workspaceId,
  resolvedTracks,
  separatingSoundTrackIdSet,
  mergingTrackIdSet,
  pendingVideoGenerationTracks,
  clampedCursorMsRef,
  volumeEditorTrackIdRef,
  seekPlaybackCursor,
  setSplitPreview,
  splitTrackAtMs,
  separateTrackAudio,
  mergeSelectedVideoTracks,
  openVolumeEditorForTrack,
  closeVolumeEditor,
}: UseWorkspaceTimelineContextMenuControllerArgs): UseWorkspaceTimelineContextMenuControllerResult => {
  const closeQuickMenu = useCallback(() => {
    kernel.current?.setMenuData(null);
  }, [kernel]);

  const resolveLiveContextMenuTrack = useCallback((contextTrackId: string): ResolvedWorkspaceTimelineTrack | null => {
    if (!contextTrackId) return null;
    const liveTrack = kernel.current?.getWorkspaceTimelineTrackById(contextTrackId, workspaceId, projectId) ?? null;
    if (!liveTrack) return null;
    // Split and audio actions intentionally keep the UI-enriched timing overlay
    // when present, because media durations may be probed only in UI. Duplicate
    // now only needs the layerId: the Kernel reads the persisted timings itself.
    return resolvedTracks.find((track) => track.layerId === liveTrack.layerId) ?? null;
  }, [kernel, projectId, resolvedTracks, workspaceId]);

  const resolveLiveSelectedTrackLayerIds = useCallback((contextTrackId: string): string[] => {
    const liveSelectedTracks = kernel.current?.getWorkspaceTimelineSelectedTracks(projectId, workspaceId) ?? [];
    const liveSelectedLayerIds = liveSelectedTracks.map((track) => track.layerId);
    if (liveSelectedLayerIds.length > 1 && liveSelectedLayerIds.includes(contextTrackId)) {
      return liveSelectedLayerIds;
    }
    return contextTrackId ? [contextTrackId] : [];
  }, [kernel, projectId, workspaceId]);

  const resolveTracksForPack = useCallback((contextTrackId: string) => {
    const selectedTrackLayerIds = resolveLiveSelectedTrackLayerIds(contextTrackId);
    const selectedTrackLayerIdSet = new Set(selectedTrackLayerIds);
    const liveProjection = kernel.current?.getWorkspaceTimelineProjection(projectId, workspaceId) ?? null;
    if (!liveProjection) return null;
    const uiResolvedTrackById = new Map(resolvedTracks.map((track) => [track.layerId, track]));
    return liveProjection.resolvedTracks
      .filter((track) => selectedTrackLayerIdSet.has(track.layerId))
      .map((track) => {
        const uiTimingTrack = uiResolvedTrackById.get(track.layerId);
        // Kernel projection is the live membership/order authority. Until media-probed
        // durations are persisted into Kernel/runtime, keep the UI timing overlay for
        // pack overlap checks so video/audio without explicit timelineEndMs match render.
        return {
          layerId: track.layerId,
          startMs: uiTimingTrack?.startMs ?? track.startMs,
          endMs: uiTimingTrack?.endMs ?? track.endMs,
          renderOrder: track.renderOrder,
        };
      })
      .sort((left, right) => {
        if (left.startMs !== right.startMs) return left.startMs - right.startMs;
        if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
        return left.layerId.localeCompare(right.layerId);
      });
  }, [kernel, projectId, resolvedTracks, resolveLiveSelectedTrackLayerIds, workspaceId]);

  const hasPackConflict = useCallback((tracksForPack: Array<{ startMs: number; endMs: number }>) => {
    if (tracksForPack.length < 2) return false;
    for (let index = 1; index < tracksForPack.length; index += 1) {
      const previous = tracksForPack[index - 1];
      const current = tracksForPack[index];
      if (!previous || !current) continue;
      if (current.startMs < previous.endMs - 1) {
        return true;
      }
    }
    return false;
  }, []);

  const handleContextMenuCutTrack = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!liveContextMenuTrack) return;
    const liveClampedCursorMs = clampedCursorMsRef.current;
    const splitCursorMs = Math.round(clamp(liveClampedCursorMs, liveContextMenuTrack.startMs, liveContextMenuTrack.endMs));
    if (
      splitCursorMs - liveContextMenuTrack.startMs < MIN_TRACK_SPAN_MS ||
      liveContextMenuTrack.endMs - splitCursorMs < MIN_TRACK_SPAN_MS
    ) {
      toast.warning(`Split point is too close to an edge. Keep at least ${MIN_TRACK_SPAN_MS}ms on each side.`);
      closeQuickMenu();
      return;
    }
    seekPlaybackCursor(splitCursorMs);
    setSplitPreview({ trackId: liveContextMenuTrack.layerId, splitMs: splitCursorMs });
    splitTrackAtMs(liveContextMenuTrack, splitCursorMs);
    closeQuickMenu();
  }, [clampedCursorMsRef, closeQuickMenu, resolveLiveContextMenuTrack, seekPlaybackCursor, setSplitPreview, splitTrackAtMs]);

  const handleContextMenuSplitSoundTrack = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!liveContextMenuTrack || liveContextMenuTrack.layerType !== 'video') return;
    if (separatingSoundTrackIdSet.has(liveContextMenuTrack.layerId)) return;
    closeQuickMenu();
    void separateTrackAudio(liveContextMenuTrack);
  }, [closeQuickMenu, resolveLiveContextMenuTrack, separateTrackAudio, separatingSoundTrackIdSet]);

  const handleContextMenuAddFadeIn = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!liveContextMenuTrack) return;
    kernel.current?.toggleTimelineTrackFade(liveContextMenuTrack.layerId, 'fade-in');
    closeQuickMenu();
  }, [closeQuickMenu, kernel, resolveLiveContextMenuTrack]);

  const handleContextMenuAddFadeOut = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!liveContextMenuTrack) return;
    kernel.current?.toggleTimelineTrackFade(liveContextMenuTrack.layerId, 'fade-out');
    closeQuickMenu();
  }, [closeQuickMenu, kernel, resolveLiveContextMenuTrack]);

  const handleContextMenuDuplicateTrack = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!liveContextMenuTrack) return;
    kernel.current?.duplicateWorkspaceTimelineTrack(projectId, workspaceId, liveContextMenuTrack.layerId);
    closeQuickMenu();
  }, [closeQuickMenu, kernel, projectId, resolveLiveContextMenuTrack, workspaceId]);

  const handleContextMenuMergeSelectedTracks = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!liveContextMenuTrack || liveContextMenuTrack.layerType !== 'video') return;
    const selectedVideoTracksForMerge = kernel.current?.getWorkspaceTimelineSelectedTracks(projectId, workspaceId).filter((track) => (
      track.layerType === 'video' && track.visible !== false
    )) ?? [];
    const canMergeSelectedVideoTracks =
      selectedVideoTracksForMerge.some((track) => track.layerId === liveContextMenuTrack.layerId) &&
      selectedVideoTracksForMerge.length > 1 &&
      !selectedVideoTracksForMerge.some((track) => mergingTrackIdSet.has(track.layerId));
    if (!canMergeSelectedVideoTracks) return;
    closeQuickMenu();
    void mergeSelectedVideoTracks();
  }, [closeQuickMenu, kernel, mergeSelectedVideoTracks, mergingTrackIdSet, projectId, resolveLiveContextMenuTrack, workspaceId]);

  const handleContextMenuPackSelectedTracks = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!kernel.current || !liveContextMenuTrack) return;
    const liveTracksForPack = resolveTracksForPack(liveContextMenuTrack.layerId);
    if (!liveTracksForPack) {
      toast.warning('Timeline is no longer available.');
      closeQuickMenu();
      return;
    }
    if (liveTracksForPack.length < 2) {
      toast.warning('Select multiple tracks to pack into one rail.');
      closeQuickMenu();
      return;
    }
    if (hasPackConflict(liveTracksForPack)) {
      toast.warning('Selected tracks overlap in time. Resolve overlaps before packing.');
      closeQuickMenu();
      return;
    }

    kernel.current.packWorkspaceTimelineTracks(
      projectId,
      workspaceId,
      liveTracksForPack.map((track) => track.layerId)
    );
    closeQuickMenu();
  }, [closeQuickMenu, hasPackConflict, kernel, projectId, resolveLiveContextMenuTrack, resolveTracksForPack, workspaceId]);

  const handleContextMenuUncompactTrack = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!kernel.current || !liveContextMenuTrack) return;
    kernel.current.uncompactWorkspaceTimelineTrack(liveContextMenuTrack.layerId);
    closeQuickMenu();
  }, [closeQuickMenu, kernel, resolveLiveContextMenuTrack]);

  const handleContextMenuDeleteTrack = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!kernel.current || !liveContextMenuTrack) return;
    const liveContextMenuDeleteTargetLayerIds = resolveLiveSelectedTrackLayerIds(liveContextMenuTrack.layerId);
    if (liveContextMenuDeleteTargetLayerIds.length === 0) return;
    liveContextMenuDeleteTargetLayerIds.forEach((layerId) => {
      kernel.current?.removeLayer(layerId);
    });
    const liveVolumeEditorTrackId = volumeEditorTrackIdRef.current;
    if (
      liveVolumeEditorTrackId &&
      liveContextMenuDeleteTargetLayerIds.includes(liveVolumeEditorTrackId)
    ) {
      closeVolumeEditor();
    }
    closeQuickMenu();
  }, [closeQuickMenu, closeVolumeEditor, kernel, resolveLiveContextMenuTrack, resolveLiveSelectedTrackLayerIds, volumeEditorTrackIdRef]);

  const handleContextMenuAdjustVolume = useCallback((contextTrackId: string) => {
    const liveContextMenuTrack = resolveLiveContextMenuTrack(contextTrackId);
    if (!liveContextMenuTrack || liveContextMenuTrack.layerType !== 'audio') return;
    openVolumeEditorForTrack(liveContextMenuTrack);
    closeQuickMenu();
  }, [closeQuickMenu, openVolumeEditorForTrack, resolveLiveContextMenuTrack]);

  const handleRemovePendingGenerationTrack = useCallback((taskId: string) => {
    if (!kernel.current || !taskId) return;
    const pendingTrackExists = pendingVideoGenerationTracks.some((track) => track.taskId === taskId);
    if (!pendingTrackExists) return;
    kernel.current.removePendingTimelineGenerationTask(projectId, taskId);
    closeQuickMenu();
  }, [closeQuickMenu, kernel, pendingVideoGenerationTracks, projectId]);

  const buildTrackMenuActions = useCallback((contextMenuTrack: ResolvedWorkspaceTimelineTrack): QuickMenuAction[] => {
    // Read the live cursor from the ref (not a reactive value) so this builder stays
    // stable across playback/scrub frames. It only runs synchronously at menu-open time,
    // so `clampedCursorMsRef.current` is the freshest cursor position anyway.
    const splitCursorMs = Math.round(clamp(clampedCursorMsRef.current, contextMenuTrack.startMs, contextMenuTrack.endMs));
    const canCutContextMenuTrack = (
      splitCursorMs - contextMenuTrack.startMs >= MIN_TRACK_SPAN_MS &&
      contextMenuTrack.endMs - splitCursorMs >= MIN_TRACK_SPAN_MS
    );
    const canSplitSoundContextMenuTrack = (
      contextMenuTrack.layerType === 'video' &&
      !separatingSoundTrackIdSet.has(contextMenuTrack.layerId)
    );
    const splitSoundContextMenuLabel = (
      contextMenuTrack.layerType === 'video' && separatingSoundTrackIdSet.has(contextMenuTrack.layerId)
        ? 'Split Sound (Processing...)'
        : 'Split Sound'
    );

    const contextMenuDeleteTargetLayerIds = resolveLiveSelectedTrackLayerIds(contextMenuTrack.layerId);
    const contextMenuDeleteLabel = contextMenuDeleteTargetLayerIds.length > 1
      ? `Delete Selected (${contextMenuDeleteTargetLayerIds.length})`
      : 'Delete';

    const projection = kernel.current?.getWorkspaceTimelineProjection(projectId, workspaceId) ?? null;
    const laneTrackCountByLaneId = (projection?.laneRows ?? []).reduce((acc, laneRow) => {
      acc.set(laneRow.laneId, laneRow.tracks.length);
      return acc;
    }, new Map<string, number>());
    const tracksForPack = resolveTracksForPack(contextMenuTrack.layerId) ?? [];
    const canUncompactContextMenuTrack = (
      contextMenuTrack.laneId !== contextMenuTrack.layerId ||
      (laneTrackCountByLaneId.get(contextMenuTrack.laneId) ?? 0) > 1
    );

    const selectedVideoTracksForMerge = kernel.current?.getWorkspaceTimelineSelectedTracks(projectId, workspaceId).filter((track) => (
      track.layerType === 'video' && track.visible !== false
    )) ?? [];
    const selectedVideoTrackIdSetForMerge = new Set(selectedVideoTracksForMerge.map((track) => track.layerId));
    const canMergeSelectedVideoTracks = (
      contextMenuTrack.layerType === 'video' &&
      selectedVideoTrackIdSetForMerge.has(contextMenuTrack.layerId) &&
      selectedVideoTracksForMerge.length > 1 &&
      !selectedVideoTracksForMerge.some((track) => mergingTrackIdSet.has(track.layerId))
    );

    const canAddEffectContextMenuTrack = contextMenuTrack.layerType !== 'audio';
    const canAdjustVolumeContextMenuTrack = contextMenuTrack.layerType === 'audio';
    const hasFadeInEffect = normalizeEffectRatio(contextMenuTrack.fadeInRatio) > TIMELINE_EFFECT_ENABLED_EPSILON;
    const hasFadeOutEffect = normalizeEffectRatio(contextMenuTrack.fadeOutRatio) > TIMELINE_EFFECT_ENABLED_EPSILON;
    const contextTrackId = contextMenuTrack.layerId;

    const actions: QuickMenuAction[] = [
      {
        label: 'Cut (Split At Cursor)',
        disabled: !canCutContextMenuTrack,
        onClick: () => handleContextMenuCutTrack(contextTrackId),
      },
      {
        label: splitSoundContextMenuLabel,
        disabled: !canSplitSoundContextMenuTrack,
        onClick: () => handleContextMenuSplitSoundTrack(contextTrackId),
      },
      {
        label: 'Duplicate',
        onClick: () => handleContextMenuDuplicateTrack(contextTrackId),
      },
    ];

    if (canAddEffectContextMenuTrack) {
      actions.push({
        label: 'Add Effect',
        onClick: () => undefined,
        subActions: [
          {
            label: hasFadeInEffect ? '✓ Fade In' : 'Fade In',
            selected: hasFadeInEffect,
            onClick: () => handleContextMenuAddFadeIn(contextTrackId),
          },
          {
            label: hasFadeOutEffect ? '✓ Fade Out' : 'Fade Out',
            selected: hasFadeOutEffect,
            onClick: () => handleContextMenuAddFadeOut(contextTrackId),
          },
        ],
      });
    }

    if (tracksForPack.length > 1) {
      actions.push({
        label: `Pack Selected (${tracksForPack.length})`,
        tooltip: hasPackConflict(tracksForPack)
          ? 'Selected tracks overlap in time. Resolve overlaps before packing.'
          : undefined,
        onClick: () => handleContextMenuPackSelectedTracks(contextTrackId),
      });
    }

    if (canUncompactContextMenuTrack) {
      actions.push({
        label: 'Uncompact Track',
        onClick: () => handleContextMenuUncompactTrack(contextTrackId),
      });
    }

    if (canMergeSelectedVideoTracks) {
      actions.push({
        label: 'Merge Selected Videos',
        onClick: () => handleContextMenuMergeSelectedTracks(contextTrackId),
      });
    }

    actions.push({
      label: contextMenuDeleteLabel,
      disabled: contextMenuDeleteTargetLayerIds.length === 0,
      separatorBefore: true,
      onClick: () => handleContextMenuDeleteTrack(contextTrackId),
    });

    if (canAdjustVolumeContextMenuTrack) {
      actions.push({
        label: 'Adjust Volume',
        onClick: () => handleContextMenuAdjustVolume(contextTrackId),
      });
    }

    return actions;
  }, [
    clampedCursorMsRef,
    handleContextMenuAddFadeIn,
    handleContextMenuAddFadeOut,
    handleContextMenuAdjustVolume,
    handleContextMenuCutTrack,
    handleContextMenuDeleteTrack,
    handleContextMenuDuplicateTrack,
    handleContextMenuMergeSelectedTracks,
    handleContextMenuPackSelectedTracks,
    handleContextMenuSplitSoundTrack,
    handleContextMenuUncompactTrack,
    hasPackConflict,
    kernel,
    mergingTrackIdSet,
    projectId,
    resolveLiveSelectedTrackLayerIds,
    resolveTracksForPack,
    separatingSoundTrackIdSet,
    workspaceId,
  ]);

  const handleTrackTimelineToolContextMenu = useCallback((
    event: React.MouseEvent<HTMLElement>,
    track: ResolvedWorkspaceTimelineTrack
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const liveTrack = kernel.current?.getWorkspaceTimelineTrackById(track.layerId, workspaceId, projectId) ?? null;
    if (!liveTrack) return;
    const selectedLayerIds = kernel.current?.readProjectRuntimeState(projectId).selectedLayersId ?? [];
    if (!selectedLayerIds.includes(liveTrack.layerId)) {
      kernel.current?.selectLayer(projectId, liveTrack.layerId, false);
    }
    const contextMenuTrack = resolvedTracks.find((resolvedTrack) => resolvedTrack.layerId === liveTrack.layerId) ?? null;
    if (!contextMenuTrack) return;
    kernel.current?.setMenuData({
      position: { x: event.clientX, y: event.clientY },
      actions: buildTrackMenuActions(contextMenuTrack),
    });
  }, [buildTrackMenuActions, kernel, projectId, resolvedTracks, workspaceId]);

  const handlePendingTrackContextMenu = useCallback((event: React.MouseEvent, taskId: string) => {
    event.preventDefault();
    event.stopPropagation();
    const pendingTrackExists = pendingVideoGenerationTracks.some((track) => track.taskId === taskId);
    if (!pendingTrackExists) return;
    kernel.current?.setMenuData({
      position: { x: event.clientX, y: event.clientY },
      actions: [
        {
          label: 'Remove This Task',
          onClick: () => handleRemovePendingGenerationTrack(taskId),
        },
      ],
    });
  }, [handleRemovePendingGenerationTrack, kernel, pendingVideoGenerationTracks]);

  return useMemo(() => ({
    handleTrackTimelineToolContextMenu,
    handlePendingTrackContextMenu,
  }), [handlePendingTrackContextMenu, handleTrackTimelineToolContextMenu]);
};
